import { fabric } from 'fabric';

function rotatePoint(point, centerPoint, angleDegrees) {
    const fabricPoint = new fabric.Point(point.x, point.y);
    const fabricCenter = new fabric.Point(centerPoint.x, centerPoint.y);
    const angleRadians = fabric.util.degreesToRadians(angleDegrees);
    return fabric.util.rotatePoint(fabricPoint, fabricCenter, angleRadians);
}

function normalizeAngleDifference(angle1, angle2) {
    let difference = angle1 - angle2;
    if (difference < -180) difference += 360;
    if (difference > 180) difference -= 360;
    return difference;
}

function rotatePoints(points, center, currentAngle, targetAngle) {
    const angleDifference = normalizeAngleDifference(currentAngle, targetAngle);
    return points.map((point) => {
        const rotated = rotatePoint(point, center, -angleDifference);
        return { x: rotated.x, y: rotated.y };
    });
}

function transformToAbsolute(relativePoints, target) {
    const matrix = target.calcTransformMatrix();
    return relativePoints.map((point) => {
        const transformed = new fabric.Point(
            target.width * point.x - target.width / 2,
            target.height * point.y - target.height / 2
        );

        return fabric.util.transformPoint(transformed, matrix);
    });
}

function mirrorPoints(points, origin, target) {
    const dx = target.x - origin.x;
    const dy = target.y - origin.y;

    if (!dx && !dy) return points;

    // Calculate reflection matrix components
    const cosTheta = (dx * dx - dy * dy) / (dx * dx + dy * dy);
    const sinTheta = (2 * dx * dy) / (dx * dx + dy * dy);

    return points.map((point) => ({
        x: cosTheta * (point.x - origin.x) + sinTheta * (point.y - origin.y) + origin.x,
        y: sinTheta * (point.x - origin.x) - cosTheta * (point.y - origin.y) + origin.y
    }));
}

function calculateControlVector(bounds, point, options) {
    const { flipX, flipY } = options;
    const originX = bounds.x + (flipX ? bounds.width : 0);
    const originY = bounds.y + (flipY ? bounds.height : 0);

    return {
        x: point.x - originX,
        y: point.y - originY
    };
}

function transformFlip(points, flipX = false, flipY = false) {
    // Extract all x coordinates into an array
    const xCoords = points.map((point) => point.x);

    // Find boundaries of x coordinates
    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);

    // Extract all y coordinates into an array
    const yCoords = points.map((point) => point.y);

    // Find boundaries of y coordinates
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    // Transform points based on flip parameters
    return points.map((point) => ({
        // If flipX is true, mirror the x coordinate across the shape's width
        x: flipX ? maxX - (point.x - minX) : point.x,
        // If flipY is true, mirror the y coordinate across the shape's height
        y: flipY ? maxY - (point.y - minY) : point.y
    }));
}

function getRotationMode(event) {
    const { original, target } = event.transform;
    const EPSILON = 0.0001; // Threshold for considering values equal

    // Check if top and left positions haven't changed significantly
    return Math.abs(original.top - target.top) < EPSILON && Math.abs(original.left - target.left) < EPSILON;
}

function _normalizePath(path) {
    return fabric.util.makePathSimpler(fabric.util.parsePath(path));
}

export {
    rotatePoint,
    rotatePoints,
    transformToAbsolute,
    mirrorPoints,
    calculateControlVector,
    getRotationMode,
    transformFlip,
    _normalizePath
};
